import { Component, forwardRef, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR, Validator, AbstractControl, ValidationErrors, NG_VALIDATORS } from '@angular/forms';

/**
 * Validates whether a phone number matches standard international calling format:
 * A '+' followed by 1-4 digit country code and exactly 9 national digits (e.g. +255777000000).
 */
export function isValidPhoneNumber(phone: string | null | undefined): boolean {
  if (!phone) return false;
  const cleaned = phone.trim().replace(/[\s\-()]/g, '');
  return /^\+[0-9]{1,4}[0-9]{9}$/.test(cleaned);
}

@Component({
  selector: 'app-phone-input',
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true
    }
  ],
  template: `
    <div class="space-y-1">
      <div
        class="flex items-center rounded-xl border bg-white transition shadow-2xs overflow-hidden"
        [ngClass]="{
          'border-slate-300 focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-500/20': !isInvalid && !disabled,
          'border-rose-400 focus-within:border-rose-500 focus-within:ring-2 focus-within:ring-rose-500/20 bg-rose-50/20': isInvalid && !disabled,
          'bg-slate-100 border-slate-200 opacity-70 cursor-not-allowed': disabled
        }"
      >
        <!-- Country Code Small Input Field -->
        <div class="flex items-center gap-1 bg-slate-50 border-r border-slate-200 px-2.5 py-2 shrink-0">
          <span class="text-xs text-slate-400 select-none">🌐</span>
          <input
            type="text"
            [(ngModel)]="countryCode"
            (ngModelChange)="onCodeChange($event)"
            [disabled]="disabled"
            list="countryCodesList"
            maxlength="5"
            placeholder="+255"
            title="Country Calling Code"
            class="w-14 bg-transparent text-xs font-bold text-slate-800 focus:outline-none text-center font-mono placeholder:text-slate-400"
          />
          <datalist id="countryCodesList">
            <option value="+255">Tanzania / Zanzibar (+255)</option>
            <option value="+254">Kenya (+254)</option>
            <option value="+256">Uganda (+256)</option>
            <option value="+250">Rwanda (+250)</option>
            <option value="+257">Burundi (+257)</option>
            <option value="+1">USA / Canada (+1)</option>
            <option value="+44">UK (+44)</option>
            <option value="+971">UAE (+971)</option>
          </datalist>
        </div>

        <!-- Remaining 9-Digit Number Input Field -->
        <div class="relative flex-1 flex items-center min-w-0">
          <input
            type="tel"
            [(ngModel)]="nationalNumber"
            (ngModelChange)="onNumberChange($event)"
            (blur)="onBlur()"
            [disabled]="disabled"
            [required]="required"
            maxlength="9"
            [placeholder]="placeholder || '777 000 000'"
            class="w-full px-3 py-2 text-xs font-semibold text-slate-900 bg-transparent focus:outline-none font-mono tracking-wider placeholder:text-slate-400"
          />

          <!-- 9-Max Validation & Digit Counter Status -->
          <div class="pr-2.5 shrink-0 flex items-center gap-1.5 select-none">
            <span
              class="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded transition-colors"
              [ngClass]="{
                'bg-emerald-100 text-emerald-800 border border-emerald-300': nationalNumber.length === 9,
                'bg-rose-50 text-rose-700 border border-rose-200': isInvalid,
                'bg-amber-50 text-amber-700 border border-amber-200': !isInvalid && nationalNumber.length > 0 && nationalNumber.length < 9,
                'bg-slate-100 text-slate-400': !isInvalid && nationalNumber.length === 0
              }"
            >
              {{ nationalNumber.length }}/9
            </span>
            @if (nationalNumber.length === 9 && !isInvalid) {
              <svg class="w-3.5 h-3.5 text-emerald-600 animate-in fade-in" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
              </svg>
            }
          </div>
        </div>
      </div>

      <!-- Helper / Error Message Hints -->
      @if (errorMessage) {
        <p class="text-[11px] text-rose-600 font-medium pl-1 flex items-center gap-1.5 animate-in fade-in">
          <svg class="w-3.5 h-3.5 text-rose-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{{ errorMessage }}</span>
        </p>
      } @else if (touched && required && !nationalNumber) {
        <p class="text-[11px] text-rose-600 font-medium pl-1 flex items-center gap-1.5 animate-in fade-in">
          <svg class="w-3.5 h-3.5 text-rose-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Phone number is required.</span>
        </p>
      } @else if (touched && nationalNumber.length > 0 && nationalNumber.length < 9) {
        <p class="text-[11px] text-amber-600 font-medium pl-1 flex items-center gap-1.5">
          <svg class="w-3.5 h-3.5 text-amber-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Remaining number must be exactly 9 digits ({{ 9 - nationalNumber.length }} more required).</span>
        </p>
      }
    </div>
  `
})
export class PhoneInputComponent implements ControlValueAccessor, Validator {
  @Input() placeholder = '777 000 000';
  @Input() required = false;
  @Input() disabled = false;
  @Input() defaultCountryCode = '+255';
  @Input() hasError = false;
  @Input() errorMessage?: string;

  @Input() set phone(value: string | null | undefined) {
    if (value !== this.fullPhone) {
      this.parseFullPhone(value);
    }
  }
  get phone(): string {
    return this.fullPhone;
  }
  @Output() phoneChange = new EventEmitter<string>();

  countryCode = '+255';
  nationalNumber = '';
  touched = false;

  private fullPhone = '';
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};

  get isInvalid(): boolean {
    if (this.disabled) return false;
    if (this.hasError || !!this.errorMessage) return true;
    if (this.touched) {
      if (this.required && !this.nationalNumber) return true;
      if (this.nationalNumber.length > 0 && this.nationalNumber.length < 9) return true;
      if (!this.countryCode || !/^\+[0-9]{1,4}$/.test(this.countryCode)) return true;
    }
    return false;
  }

  markAsTouched(): void {
    this.touched = true;
    this.onTouched();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const val = control.value;
    if (this.required && (!val || !this.nationalNumber)) {
      return { required: true };
    }
    if (this.nationalNumber && this.nationalNumber.length < 9) {
      return { minlength: { requiredLength: 9, actualLength: this.nationalNumber.length } };
    }
    if (this.countryCode && !/^\+[0-9]{1,4}$/.test(this.countryCode)) {
      return { invalidCountryCode: true };
    }
    return null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  writeValue(val: string | null | undefined): void {
    this.parseFullPhone(val);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onCodeChange(val: string): void {
    let code = (val || '').trim();
    if (code && !code.startsWith('+')) {
      code = '+' + code.replace(/\D/g, '');
    } else if (code) {
      code = '+' + code.substring(1).replace(/\D/g, '');
    }
    this.countryCode = code;
    this.emitConcatenatedValue();
    this.onValidatorChange();
  }

  onNumberChange(val: string): void {
    let digits = (val || '').replace(/\D/g, '');

    // Strip leading zero if user entered 0777...
    if (digits.startsWith('0')) {
      digits = digits.substring(1);
    }

    // Maximum 9 digits allowed
    if (digits.length > 9) {
      digits = digits.substring(0, 9);
    }

    this.nationalNumber = digits;
    this.emitConcatenatedValue();
    this.onValidatorChange();
  }

  onBlur(): void {
    this.touched = true;
    this.onTouched();
    this.onValidatorChange();
  }

  private emitConcatenatedValue(): void {
    const full = this.nationalNumber ? `${this.countryCode || this.defaultCountryCode}${this.nationalNumber}` : '';
    this.fullPhone = full;
    this.phoneChange.emit(full);
    this.onChange(full);
  }

  private parseFullPhone(raw: string | null | undefined): void {
    if (!raw) {
      this.countryCode = this.defaultCountryCode;
      this.nationalNumber = '';
      this.fullPhone = '';
      return;
    }

    this.fullPhone = raw;
    const cleaned = raw.trim().replace(/[\s\-()]/g, '');

    if (cleaned.startsWith('+')) {
      const digits = cleaned.substring(1);
      if (digits.length > 9) {
        this.nationalNumber = digits.slice(-9);
        this.countryCode = '+' + digits.slice(0, -9);
      } else {
        this.countryCode = this.defaultCountryCode;
        this.nationalNumber = digits;
      }
    } else if (cleaned.startsWith('00')) {
      const digits = cleaned.substring(2);
      if (digits.length > 9) {
        this.nationalNumber = digits.slice(-9);
        this.countryCode = '+' + digits.slice(0, -9);
      } else {
        this.countryCode = this.defaultCountryCode;
        this.nationalNumber = digits;
      }
    } else if (cleaned.startsWith('255') && cleaned.length > 9) {
      this.countryCode = '+255';
      this.nationalNumber = cleaned.slice(-9);
    } else if (cleaned.startsWith('0') && cleaned.length >= 10) {
      this.countryCode = this.defaultCountryCode;
      this.nationalNumber = cleaned.substring(1).slice(0, 9);
    } else {
      this.countryCode = this.defaultCountryCode;
      this.nationalNumber = cleaned.slice(-9);
    }

    // Ensure national number never exceeds 9 digits
    if (this.nationalNumber.length > 9) {
      this.nationalNumber = this.nationalNumber.substring(0, 9);
    }
  }
}
